import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Col, Divider, Row, Space, Tag, Typography, Button, List, Skeleton } from 'antd';
import { getReportNode } from '../services/report-nodeService';
import { listQuestionsAll } from '../../questions/services/questionService';
import { listAnswersAll } from '../../answers/services/answerService';

const letterForIndex = (idx: number) => String.fromCharCode('A'.charCodeAt(0) + idx);

const questionTypeMap: Record<string, { label: string; color: string }> = {
  MULTIPLE: { label: 'Çoktan Seçmeli', color: 'blue' },
  FREETEXT: { label: 'Serbest Metin', color: 'purple' },
  NETWORK_TYPE: { label: 'Şebeke Tipi', color: 'cyan' },
  TITLE: { label: 'Title', color: 'default' },
  ADDRESS: { label: 'Address', color: 'default' },
  SGK_REGISTRATION_NUMBER: { label: 'SGK Registration Number', color: 'default' },
  PHONE: { label: 'Phone', color: 'default' },
  CONTRACT_ID: { label: 'Contract ID', color: 'default' },
  EMAIL: { label: 'Email', color: 'default' },
  IDENTITY_NUMBER: { label: 'TC Kimlik No', color: 'cyan' },
  REGISTRATION_DATE: { label: 'Kalıcı Kayıt Tarihi', color: 'cyan' },
  REPORT_DATE: { label: 'Rapor Tarihi', color: 'gold' },
  CONTROL_START_DATE: { label: 'Kontrol Başlangıç Tarihi', color: 'gold' },
  CONTROL_END_DATE: { label: 'Kontrol Bitiş Tarihi', color: 'gold' },
  NEXT_CONTROL_DATE: { label: 'Sonraki Kontrol Tarihi', color: 'gold' },
  NUMBER: { label: 'Sayı', color: 'green' },
  DATE: { label: 'Tarih', color: 'gold' },
  TIME: { label: 'Saat', color: 'geekblue' },
  YEAR: { label: 'Yıl', color: 'cyan' },
  IMAGE: { label: 'Görsel', color: 'magenta' },
  HVAC: { label: 'Havalandırma/Klima', color: 'orange' },
  PLUMBING: { label: 'Tesisat', color: 'volcano' },
  HYDRANT: { label: 'Hidrant', color: 'red' },
  CABINET: { label: 'Dolap', color: 'magenta' },
  FUNCTION: { label: 'Fonksiyon', color: 'lime' },
  STARTED_AT: { label: 'Başlangıç Saati', color: 'geekblue' },
  FINISHED_AT: { label: 'Bitiş Saati', color: 'geekblue' },
};

const isQuestionNodeType = (type?: string) => {
  const normalized = (type ?? '').toUpperCase();
  return normalized === 'PANEL_CONTROL' || normalized === 'STANDARD' || normalized === 'APPROVAL' || normalized === 'CONTROL_CRITERIA_AND_TESTS';
};

export default function ReportNodeViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [node, setNode] = useState<any>();
  const [items, setItems] = useState<Array<{ question: any; answers: any[] }>>([]);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const nodeRes = await getReportNode(Number(id));
        setNode(nodeRes);
        if (!isQuestionNodeType(nodeRes?.type)) {
          setItems([]);
          return;
        }
        const questions = await listQuestionsAll('nodeId.equals=' + id, 0, 1000);
        const answersByQuestion: Record<number, any[]> = {};
        await Promise.all(
          questions.map(async (q: any) => {
            const ans = await listAnswersAll('questionId.equals=' + q.id);
            answersByQuestion[q.id] = ans?.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
          })
        );
        const data = questions
          .sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
          .map((q: any) => ({ question: q, answers: answersByQuestion[q.id] || [] }));
        setItems(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const titleExtra = useMemo(() => (
    <Space>
      <Button onClick={() => navigate(-1)}>Geri</Button>
    </Space>
  ), [navigate]);

  return (
    <div>
      <Card className='br-30px' title={<Typography.Title level={3} style={{ margin: 0 }}>Rapor Yapısı</Typography.Title>} extra={titleExtra}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : (
          <>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>{node?.title}</Typography.Title>
            <Typography.Text type='secondary'>{node?.code}{node?.description ? ' • ' + node?.description : ''}</Typography.Text>
            <Divider />
            <Row>
              <Col span={24}>
                <List
                  dataSource={items}
                  renderItem={(it, index) => {
                    const q = it.question;
                    const typeInfo = q?.questionType ? questionTypeMap[q.questionType] : undefined;
                    return (
                      <List.Item style={{ display: 'block' }}>
                        <div style={{ marginBottom: 8 }}>
                          <Space size={8} wrap>
                            <Typography.Text strong>{index + 1}.</Typography.Text>
                            <Typography.Text strong>{q?.title}</Typography.Text>
                            {typeInfo ? <Tag color={typeInfo.color}>{typeInfo.label}</Tag> : null}
                            {q?.isRequired ? <Tag color='red'>Zorunlu</Tag> : null}
                            {q?.isCritical ? <Tag color='volcano'>Kritik</Tag> : null}
                          </Space>
                        </div>
                        {it.answers && it.answers.length > 0 ? (
                          <div style={{ paddingLeft: 24 }}>
                            {it.answers.map((a: any, i: number) => (
                              <div key={a.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                                <Typography.Text style={{ width: 20 }}>{letterForIndex(i)})</Typography.Text>
                                <Typography.Text>{a.title}</Typography.Text>
                                <Space size={6} style={{ marginLeft: 8 }}>
                                  {a.isTrue ? <Tag color='green'>Doğru</Tag> : null}
                                  {a.isDefault ? <Tag color='blue'>Varsayılan</Tag> : null}
                                </Space>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ paddingLeft: 24 }}>
                            <Typography.Text type='secondary'>Cevap: __________</Typography.Text>
                          </div>
                        )}
                      </List.Item>
                    );
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      </Card>
    </div>
  );
}
